import { AdminApi, DropdownElement } from 'api';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'ui/atoms/button';
import Checkbox from 'ui/atoms/checkbox';
import Grid, { Col } from 'ui/atoms/grid';
import Header from 'ui/atoms/header';
import Section from 'ui/atoms/section';
import Translate from 'ui/atoms/translate';
import { VoucherFields } from '..';
import { useProductValidity } from './hooks';
import Spacer from 'ui/atoms/spacer';
import useApiCall from 'src/hooks/use-api-call';

interface VoucherUsesByInvestorProps {
  Form: any;
  values: VoucherFields;
  selectedValidityProducts: string[] | undefined;
  selectedDistributionPlatform: string;
  onChangeValidity: (products: DropdownElement[] | string) => void;
}

export enum ProductsOptions {
  ALL_PRODUCTS = 'allProducts',
  SPECIFIC = 'specific',
}

const MAX_PRODUCTS_SHOWN = 10;

const ProductValidity: FunctionComponent<VoucherUsesByInvestorProps> = ({
  Form,
  values,
  selectedValidityProducts,
  selectedDistributionPlatform,
  onChangeValidity,
}) => {
  const [productsOfSelectedDistributionPlatform, setProductsOfSelectedDistributionPlatform] = useState<
    DropdownElement[]
  >([]);

  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const fetchProducts = useCallback(() => {
    withApi(async () => {
      const response = await adminApi.adminProductsDropdownList({
        distributionPlatformId: selectedDistributionPlatform,
      });
      setProductsOfSelectedDistributionPlatform(response.results || []);
    });
  }, [withApi, adminApi, selectedDistributionPlatform]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const {
    selectedProducts,
    firstColumnProducts,
    secondColumnProducts,
    showAllProducts,
    setShowAllProducts,
    onChangeProduct,
    onProductsValidityChange,
  } = useProductValidity(
    productsOfSelectedDistributionPlatform,
    MAX_PRODUCTS_SHOWN,
    values,
    selectedValidityProducts,
    onChangeValidity,
  );

  const renderColumn = (products: DropdownElement[]) => (
    <Col width={6}>
      {products.length > 0 &&
        products.map((product: DropdownElement) => (
          <Checkbox
            key={product.id}
            checked={selectedProducts.includes(product)}
            onChange={(evt) => onChangeProduct(product, (evt.target as HTMLInputElement).checked)}
          >
            {product.name}
          </Checkbox>
        ))}
    </Col>
  );

  return (
    <Section>
      <Form.Group name="validityProducts" required={true}>
        <Section spacing="none">
          <Header spacing="medium">
            <Translate name="createVoucherForm.validity.label" />
          </Header>
          <Form.GroupToggle>
            {Object.values(ProductsOptions).map((option) => (
              <Form.Radio value={option} key={option} onClick={onProductsValidityChange}>
                <Translate name={`createVoucherForm.validity.${option}`} />
              </Form.Radio>
            ))}
          </Form.GroupToggle>
          {values.validityProducts === 'specific' && (
            <>
              <Spacer y={4} />
              <Section>
                <Grid>
                  {renderColumn(firstColumnProducts)}
                  {renderColumn(secondColumnProducts)}
                </Grid>
                {productsOfSelectedDistributionPlatform &&
                  productsOfSelectedDistributionPlatform.length > MAX_PRODUCTS_SHOWN &&
                  !showAllProducts && (
                    <Button variant="link" onClick={() => setShowAllProducts(true)}>
                      <Translate name={`createVoucherForm.showAll`} />
                    </Button>
                  )}
              </Section>
            </>
          )}
        </Section>
      </Form.Group>
    </Section>
  );
};

export default ProductValidity;
